import b4a from 'b4a'
import { domainHash, sign } from '@pine/crypto'

/**
 * Client for the chat directory.
 *
 * The server keeps, per account, the list of rooms that account is in: an id,
 * a title, the members, and the account's own profile card.
 *
 * This exists because a recovery key used to bring back an account and nothing
 * else. Everything the account was part of lived on the device that was lost,
 * so restoring produced an empty app with no way to even name what was gone.
 *
 * ── the vault ──────────────────────────────────────────────────────────────
 *
 * Alongside that plain list travels one opaque string: the room keys, sealed
 * under a key derived from the account seed (client._sealVault). The server
 * stores and returns it without being able to read a byte of it, and cannot
 * be given the ability — the seed never leaves the device.
 *
 * The first version of this deliberately had no vault, and restoring meant
 * asking the other members for a way back in. That is safer on paper and was
 * unusable in practice: a chat stayed dead until somebody in it happened to be
 * online, which in a two-person conversation means "until your friend opens
 * the app". With the vault a restored device has its chats back at once, and
 * asking the members is what it falls back to.
 *
 * The trade, stated plainly: an operator sees the shape of who talks to whom
 * and what the rooms are called, and holds ciphertext that the account seed
 * opens. It buys nothing on its own — anyone holding that seed can already ask
 * the members for the same keys, signing as the account they now own — but it
 * does mean a stolen backup plus a later-leaked seed reads old rooms, where
 * before it would have needed a member online and willing. Settings has a
 * switch that stops publishing it; see client._sealVault.
 */

const DEFAULT_TIMEOUT = 12000

/** Must stay byte-identical to digestRooms() in server/relay/lib/directory.js. */
export function digestRooms (rooms) {
  const parts = []
  for (const room of rooms || []) {
    parts.push(String(room.id || ''))
    parts.push(String(room.name || ''))
    parts.push(String(room.type ?? ''))
    parts.push((room.members || [])
      .map((m) => String(m.account || '') + ':' + String(m.username || ''))
      .join(','))
  }
  return domainHash('pine/rooms/digest/v1', ...parts)
}

/**
 * The profile the account carries with it.
 *
 * Held here so a restored device shows the right person straight away instead
 * of an empty name and a stranger's blank card. Name, bio, handle and photo:
 * the same fields the account already publishes to anyone it shares a room
 * with, so nothing new is exposed to the people you talk to — only to the
 * server.
 */
export function digestProfile (profile) {
  const p = profile || {}
  const name = String(p.name || '')
  const bio = String(p.bio || '')
  const username = String(p.username || '')
  const avatar = String(p.avatar || '')
  const at = profileTime(p.updatedAt)
  // Same rule as digestDirectory below: the presence of the newer field picks
  // the version, so a build that has never heard of avatars — or of edit
  // times — signs exactly the bytes it always did.
  if (at) return domainHash('pine/rooms/profile/v3', name, bio, username, avatar, String(at))
  if (!avatar) return domainHash('pine/rooms/profile/v1', name, bio, username)
  return domainHash('pine/rooms/profile/v2', name, bio, username, avatar)
}

/** An edit time as both sides will write it: a whole number of ms, or 0. */
export function profileTime (value) {
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0
}

/**
 * What the signature covers.
 *
 * Versioned by which fields are present, so a client from before the profile
 * or the vault existed keeps signing exactly what it used to and its publishes
 * keep verifying. Do not fold either into digestRooms() to avoid this: that
 * would reject every older client silently.
 */
export function digestDirectory ({ rooms, profile, vault } = {}) {
  const base = digestRooms(rooms)
  const withProfile = profile
    ? domainHash('pine/rooms/publish/v2', base, digestProfile(profile))
    : base
  // Absent means "nothing to say about the vault"; an empty string means "I
  // have one and it is empty" — which is how key backup is switched off. They
  // sign differently so the second cannot be turned into the first in flight.
  if (vault === undefined || vault === null) return withProfile
  return domainHash('pine/rooms/publish/v3', withProfile, String(vault))
}

export function publishSignable ({ account, at, digest }) {
  return domainHash('pine/rooms/publish/v1', account, String(at), digest)
}

export function fetchSignable ({ account, at }) {
  return domainHash('pine/rooms/fetch/v1', account, String(at))
}

export function revokeSignable ({ account, at, device }) {
  return domainHash('pine/rooms/revoke/v1', account, String(at), String(device || ''))
}

export class RoomDirectoryClient {
  constructor ({ url, identity }) {
    this.url = (url || '').replace(/\/+$/, '')
    this.identity = identity
  }

  get enabled () {
    return !!this.url
  }

  async _post (path, body, timeout = DEFAULT_TIMEOUT) {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeout)
    try {
      const response = await fetch(this.url + path, {
        method: 'POST',
        // text/plain is CORS-safelisted, so no preflight — the Android build
        // is cross-origin and a preflight is one more thing to go wrong. The
        // server parses the body without consulting this header.
        headers: { 'content-type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify(body),
        signal: controller.signal
      })
      const payload = await response.json().catch(() => ({}))
      return { status: response.status, body: payload }
    } finally {
      clearTimeout(timer)
    }
  }

  /**
   * Replace what the server holds for this account.
   *
   * Tries the current shape, and on a rejected signature falls back to the one
   * a relay from before the vault understands. That case is a real one and not
   * a hypothetical: phones update themselves and servers are updated by hand,
   * so for a while some of each are talking to the other. Without the fallback
   * a phone that updated first would silently stop backing up its chat list
   * until somebody got round to the relay.
   */
  async publish (rooms, profile = null, device = null, vault = null) {
    if (!this.enabled) return false

    const full = await this._publish(rooms, profile, device, vault)
    if (full.ok || full.status !== 403) return full.ok

    // 403 is "that signature does not verify", which is exactly what an older
    // relay says when it hashes fewer fields than we signed. Retried in the
    // old shape — no vault, no photo — rather than dropping the publish. The
    // full attempt is made again next time, so an upgrade takes effect at once.
    const stripped = profile ? { ...profile, avatar: '' } : null
    const back = await this._publish(rooms, stripped, device, null)
    return back.ok
  }

  async _publish (rooms, profile, device, vault) {
    const at = Date.now()
    const account = this.identity.id
    // null when the caller has no opinion — an older path, or a test. Anything
    // else, '' included, is this account stating what its vault now is.
    const sealed = vault === null || vault === undefined ? undefined : String(vault)
    const signature = sign(
      publishSignable({ account, at, digest: digestDirectory({ rooms, profile, vault: sealed }) }),
      this.identity.secretKey
    )
    try {
      const result = await this._post('/api/rooms', {
        account,
        at,
        rooms,
        profile: profile || undefined,
        device: device || undefined,
        vault: sealed,
        signature: b4a.toString(signature, 'hex')
      }, 30000)
      return { ok: result.status === 200, status: result.status }
    } catch {
      return { ok: false, status: 0 }
    }
  }

  /** @returns { rooms, profile }, or null when the server could not be asked. */
  async fetch () {
    if (!this.enabled) return null
    const at = Date.now()
    const account = this.identity.id
    const signature = sign(fetchSignable({ account, at }), this.identity.secretKey)
    try {
      const result = await this._post('/api/rooms/fetch', {
        account,
        at,
        signature: b4a.toString(signature, 'hex')
      })
      if (result.status !== 200) return null
      return {
        rooms: Array.isArray(result.body.rooms) ? result.body.rooms : [],
        profile: result.body.profile || null,
        devices: Array.isArray(result.body.devices) ? result.body.devices : [],
        vault: typeof result.body.vault === 'string' ? result.body.vault : ''
      }
    } catch {
      return null
    }
  }

  /** Drop one device from the list. */
  async revoke (device) {
    if (!this.enabled || !device) return false
    const at = Date.now()
    const account = this.identity.id
    const signature = sign(revokeSignable({ account, at, device }), this.identity.secretKey)
    try {
      const result = await this._post('/api/rooms/revoke', {
        account,
        at,
        device,
        signature: b4a.toString(signature, 'hex')
      })
      return result.status === 200
    } catch {
      return false
    }
  }

  /** Erase the list. Signing out for good should not leave it behind. */
  async forget () {
    if (!this.enabled) return false
    const at = Date.now()
    const account = this.identity.id
    const signature = sign(fetchSignable({ account, at }), this.identity.secretKey)
    try {
      const result = await this._post('/api/rooms/forget', {
        account,
        at,
        signature: b4a.toString(signature, 'hex')
      })
      return result.status === 200
    } catch {
      return false
    }
  }
}
